import React, { useState, useEffect } from 'react';
import tokenUsageService from '../../services/tokenUsageService';
import { Card } from '../common';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './TokenUsageDashboard.css';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const { cost, count } = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <div className="tooltip-label">{label}</div>
      <div>예상 비용: <strong>${cost}</strong></div>
      <div>요청 수: <strong>{count}건</strong></div>
    </div>
  );
};

const TokenUsageDashboard = ({ isAdmin = false }) => {
  const [usageData, setUsageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartMode, setChartMode] = useState('daily');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  useEffect(() => {
    if (isAdmin) {
      // 일별 모드에서 시작일만 있고 종료일 없으면 조회 안 함
      if (chartMode === 'daily' && dateRange.startDate && !dateRange.endDate) return;
      loadUsageData();
    } else {
      setError('관리자 권한이 필요합니다.');
      setLoading(false);
    }
  }, [dateRange, isAdmin, chartMode]);

  const loadUsageData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = getDateParams();

      // 모든 사용자 사용량 조회
      const allUsersResponse = await tokenUsageService.getAllUsersUsage(params);
      
      // 모든 사용량 데이터를 하나의 배열로 합치고 시간순 정렬
      const allUsageData = [];
      (allUsersResponse.data.users || []).forEach(user => {
        allUsageData.push(...user.usage);
      });
      
      // 생성 시간순으로 정렬 (최신부터)
      allUsageData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setUsageData(allUsageData);

    } catch (err) {
      console.error('토큰 사용량 조회 실패:', err);
      setError('토큰 사용량을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field, value) => {
    if (chartMode === 'daily') {
      setDateRange(prev => {
        const next = { ...prev, [field]: value };
        // 시작일이 바뀌면 종료일 초기화
        if (field === 'startDate') {
          next.endDate = '';
        }
        // 종료일이 다른 달이면 보정
        if (next.startDate && next.endDate) {
          const startMonth = next.startDate.substring(0, 7);
          const endMonth = next.endDate.substring(0, 7);
          if (startMonth !== endMonth) {
            const lastDay = new Date(startMonth.slice(0, 4), startMonth.slice(5, 7), 0).getDate();
            next.endDate = `${startMonth}-${String(lastDay).padStart(2, '0')}`;
          }
        }
        return next;
      });
    } else {
      setDateRange(prev => ({ ...prev, [field]: value }));
    }
  };

  const getDateParams = () => {
    const { startDate, endDate } = dateRange;
    const params = {};
    if (chartMode === 'monthly') {
      if (startDate) params.startDate = `${startDate}-01T00:00:00.000Z`;
      if (endDate) {
        const lastDay = new Date(endDate.slice(0, 4), endDate.slice(5, 7), 0).getDate();
        params.endDate = `${endDate}-${lastDay}T23:59:59.999Z`;
      }
    } else {
      if (startDate) params.startDate = `${startDate}T00:00:00.000Z`;
      if (endDate) params.endDate = `${endDate}T23:59:59.999Z`;
    }
    return params;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const getTotalTokens = () => {
    return usageData.reduce((sum, item) => sum + (item.totalTokens || 0), 0);
  };

  const getTotalRequests = () => {
    return usageData.length;
  };

  const getOverallSuccessRate = () => {
    if (usageData.length === 0) return 0;
    const successCount = usageData.filter(item => item.status === 'success').length;
    return Math.round((successCount / usageData.length) * 100);
  };

  const calcCost = (inputTokens, outputTokens) => {
    const inputCost = (inputTokens / 1000) * 0.003;
    const outputCost = (outputTokens / 1000) * 0.015;
    return (inputCost + outputCost).toFixed(4);
  };

  const getTotalCost = () => {
    return usageData.reduce((sum, item) => {
      return sum + parseFloat(calcCost(item.inputTokens || 0, item.outputTokens || 0));
    }, 0).toFixed(4);
  };

  const getUniqueUsers = () => {
    const uniqueUsers = new Set(usageData.map(item => item.userId));
    return uniqueUsers.size;
  };

  const handleChartModeChange = (mode) => {
    setChartMode(mode);
    setDateRange({ startDate: '', endDate: '' });
  };

  const getCostChartData = () => {
    const grouped = {};
    usageData.forEach(item => {
      const d = new Date(item.createdAt);
      const key = chartMode === 'daily'
        ? d.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })
        : d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit' });
      if (!grouped[key]) grouped[key] = { cost: 0, count: 0 };
      grouped[key].cost += parseFloat(calcCost(item.inputTokens || 0, item.outputTokens || 0));
      grouped[key].count += 1;
    });
    return Object.entries(grouped)
      .map(([date, { cost, count }]) => ({ date, cost: parseFloat(cost.toFixed(4)), count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  if (loading) {
    return (
      <div className="token-usage-dashboard">
        <div className="loading-spinner">토큰 사용량을 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="token-usage-dashboard">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="token-usage-dashboard">
      <div className="dashboard-header">
        <h2>토큰 사용량 모니터링</h2>
        
        <div className="date-range-selector">
          {chartMode === 'monthly' ? (
            <>
              <label>시작 월</label>
              <input type="month" value={dateRange.startDate} onChange={(e) => handleDateRangeChange('startDate', e.target.value)} />
              <span>~</span>
              <label>종료 월</label>
              <input type="month" value={dateRange.endDate} onChange={(e) => handleDateRangeChange('endDate', e.target.value)} />
            </>
          ) : (
            <>
              <label>시작일</label>
              <input type="date" value={dateRange.startDate} onChange={(e) => handleDateRangeChange('startDate', e.target.value)} />
              <span>~</span>
              <label>종료일</label>
              <input
                type="date"
                value={dateRange.endDate}
                min={dateRange.startDate}
                max={dateRange.startDate ? `${dateRange.startDate.substring(0, 7)}-${String(new Date(dateRange.startDate.slice(0,4), dateRange.startDate.slice(5,7), 0).getDate()).padStart(2,'0')}` : ''}
                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
              />
            </>
          )}
        </div>
      </div>

      {/* 요약 통계 */}
      <div className="usage-summary">
        <Card className="summary-card">
          <h3>총 토큰 사용량</h3>
          <div className="stat-value">{getTotalTokens().toLocaleString()}</div>
        </Card>
        
        <Card className="summary-card">
          <h3>총 요청 수</h3>
          <div className="stat-value">{getTotalRequests()}</div>
        </Card>
        
        <Card className="summary-card">
          <h3>전체 성공률</h3>
          <div className="stat-value">{getOverallSuccessRate()}%</div>
        </Card>
        
        <Card className="summary-card">
          <h3>사용자 수</h3>
          <div className="stat-value">{getUniqueUsers()}</div>
        </Card>

        <Card className="summary-card">
          <h3>총 예상 비용</h3>
          <div className="stat-value">${getTotalCost()}</div>
        </Card>
      </div>

      {/* 비용 그래프 */}
      <Card className="usage-chart">
        <div className="chart-header">
          <h3>예상 비용 추이 (USD)</h3>
          <div className="chart-mode-toggle">
            <button
              className={chartMode === 'daily' ? 'active' : ''}
              onClick={() => handleChartModeChange('daily')}
            >일별</button>
            <button
              className={chartMode === 'monthly' ? 'active' : ''}
              onClick={() => handleChartModeChange('monthly')}
            >월별</button>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={getCostChartData()} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e1e8ed" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="cost" fill="#3498db" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* 사용량 히스토리 */}
      <Card className="usage-history">
        <h3>토큰 사용량 히스토리 (최신순)</h3>
        {usageData.length === 0 ? (
          <div className="no-data">사용량 데이터가 없습니다.</div>
        ) : (
          <div className="usage-table">
            <table>
              <colgroup>
                <col className="col-datetime" />
                <col className="col-user" />
                <col className="col-report" />
                <col className="col-model" />
                <col className="col-input" />
                <col className="col-output" />
                <col className="col-total" />
                <col className="col-duration" />
                <col className="col-status" />
                <col className="col-cost" />
              </colgroup>
              <thead>
                <tr>
                  <th>날짜/시간</th>
                  <th>사용자</th>
                  <th>리포트 ID</th>
                  <th>모델</th>
                  <th>입력 토큰</th>
                  <th>출력 토큰</th>
                  <th>총 토큰</th>
                  <th>소요 시간</th>
                  <th>상태</th>
                  <th>예상 비용</th>
                </tr>
              </thead>
              <tbody>
                {usageData.map((item) => (
                  <tr key={item.usageId}>
                    <td>{formatDate(item.createdAt)}</td>
                    <td className="cell-mono" title={item.userName}>{item.userName || '-'}</td>
                    <td className="cell-mono" title={item.reportId}>{item.reportId || '-'}</td>
                    <td className="cell-model">{item.modelId.split('.').pop()}</td>
                    <td>{(item.inputTokens || 0).toLocaleString()}</td>
                    <td>{(item.outputTokens || 0).toLocaleString()}</td>
                    <td className="cell-total">{(item.totalTokens || 0).toLocaleString()}</td>
                    <td>{item.requestDuration}ms</td>
                    <td>
                      <span className={`status-badge ${item.status}`}>
                        {item.status === 'success' ? '성공' : '실패'}
                      </span>
                    </td>
                    <td className="cell-cost">${calcCost(item.inputTokens || 0, item.outputTokens || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TokenUsageDashboard;